import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './BananaSequencing.css';
import Button from '@albertsons/uds/molecule/Button';
import EditableTable from './EditableTable';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import axios from 'axios';
import { Environment } from '../utils/Environment';
import { useMsal } from '@azure/msal-react';
import { authProvider } from '../utils/authProvider';
import { motion } from 'framer-motion';
import { Switch, FormControlLabel, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { RefreshCw, Info, ArrowUpCircle, RotateCcw, Lock, AlertTriangle, XCircle } from 'react-feather';

type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

interface BananaSequencingProps {
  selectedOrg?: string;
}

interface CICDetail {
  OnHand: number;
  ItemId: string;
}

interface BananaRoom {
  LocationId: string;
  NewSortSequence: string;
  PickExecutionSequence: string;
  PickAllocationSequence: string;
  locked: string;
  ConditionCode: string;
  CICDetails: CICDetail[];
}

interface TableRowData {
  rowKey: string;
  locationId: string;
  itemIds: string[];
  cicDetails: CICDetail[];

  currentSequence: string;
  newSequence: string;
  currentAllocationSequence: string;
  isLocked: boolean;
  conditionCode: string;
  wasAutoFilled?: boolean;

  onHand: number;
  noInventory: boolean;
}

const HEADERS = [
  'Location ID',
  'On Hand',
  'Condition Code(s)',
  'Current Pick Allocation Sequence',
  'Current Pick Execution Sequence',
  'New Sequence *'
];

const BananaSequencing: React.FC<BananaSequencingProps> = ({ selectedOrg }) => {
  const { accounts } = useMsal();
  const auth = authProvider();

  const [allData, setAllData] = useState<TableRowData[]>([]);
  const [tableData, setTableData] = useState<(string | JSX.Element)[][]>([]);
  const [showLocked, setShowLocked] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const isResettingRef = React.useRef(false);
  const hasShownDuplicateRef = React.useRef(false);
  const [dataVersion, setDataVersion] = useState(0);
  const prevOrgRef = React.useRef<string | undefined>(undefined);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const isLoadingRef = React.useRef(false);
  const [hasConfirmedUpdate, setHasConfirmedUpdate] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as SnackbarSeverity
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isUpdateDisabled, setIsUpdateDisabled] = useState(true);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  useEffect(() => {
    if (selectedOrg) {
      setShowLocked(false);
      setShowEmpty(false);
    }
  }, [selectedOrg]);

  /* -------------------- Helpers -------------------- */

  const showToast = useCallback((message: string, severity: SnackbarSeverity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const getUserIdOrFail = useCallback(() => {
    const userId = auth.userId;
    if (!userId) {
      showToast('User ID not found.', 'error');
      return null;
    }
    return userId;
  }, [auth.userId, showToast]);

  /* -------------------- API -------------------- */

  const fetchBananaRooms = useCallback(async () => {
  if (isLoadingRef.current) return;
  if (!selectedOrg) return;

  const userId = getUserIdOrFail();
  if (!userId) return;

  setValidationErrors({});
  setTopLevelError(null);
  setShowLocked(false);
  setShowEmpty(false);

  setIsLoading(true);
  isLoadingRef.current = true;

  try {
    const apiUrl = `${Environment.getFetchBananaRoomsAPI()}?dcCode=${selectedOrg}&userId=${userId}`;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const response = await axios.post<BananaRoom[]>(apiUrl, {}, {
      timeout: 5000,
      signal: controller.signal
    });

    const formatted: TableRowData[] = response.data.map(
  (room): TableRowData => {
    const cicDetails = room.CICDetails || [];

    return {
      rowKey: room.LocationId,
      locationId: room.LocationId,

      itemIds: cicDetails.map(c => c.ItemId),
      cicDetails,

      currentSequence: room.PickExecutionSequence,
      newSequence: '',
      currentAllocationSequence: room.PickAllocationSequence,
      isLocked: room.locked === 'true',
      conditionCode: room.ConditionCode,

      onHand: cicDetails.reduce(
        (sum, item) => sum + item.OnHand,
        0
      ),

      noInventory: cicDetails.length === 0
    };
  }
);



    setAllData(formatted);
    setDataVersion(prev => prev + 1);

    showToast('Data refreshed successfully!');
  } catch (error: any) {
    if (error.name !== 'CanceledError') {
      showToast('Failed to fetch banana rooms.', 'error');
    }
  } finally {
    setIsLoading(false);
    isLoadingRef.current = false;
  }
}, [selectedOrg, getUserIdOrFail, showToast]);

  const proceedWithUpdate = async (dataToUpdate: TableRowData[]) => {
    if (isUpdateDisabled) return showToast('Validation errors exist.', 'error');
    if (!selectedOrg) return showToast('Organisation not selected.', 'error');

    const userId = getUserIdOrFail();
    if (!userId) return;

    // Filter out locked and empty on-hand rows before creating the request body
    const processedLocations = new Set<string>();

const dataToSend = dataToUpdate.filter(row => {
  
if (!row.newSequence?.trim()) {
    return false;
  }

  if (
    row.isLocked ||
    (!row.noInventory && row.onHand <= 0)
) {
    return false;
}

  if (processedLocations.has(row.locationId)) {
    return false;
  }

  processedLocations.add(row.locationId);
  return true;
});

    try {
      const requestBody = {
        data: dataToSend.map((row) => ({
          LocationId: row.locationId,
          PickExecutionSequence: row.newSequence || '',
          PickAllocationSequence: row.newSequence || '',
        })),
      };

      if (requestBody.data.length === 0) {
        return showToast('No data to update.', 'info');
      }

      const apiUrl = `${Environment.updateNewSeqAPI()}?dcCode=${selectedOrg}&userId=${userId}`;
      await axios.post(apiUrl, requestBody);
      setHasConfirmedUpdate(false);
      showToast('Update successful! Refreshing...');
      setShowLocked(false);
      setShowEmpty(false);
      await fetchBananaRooms();
    } catch (e) {
      console.error(e);
      showToast('Update failed.', 'error');
    }
  };

  const handleUpdate = async () => {
  if (!validateAllFields()) {
    showToast('Please fix the validation errors before updating.', 'error');
    return;
  }

  // User already confirmed, now proceed with update
  if (hasConfirmedUpdate) {
    setHasConfirmedUpdate(false);
    proceedWithUpdate(filteredData);
    return;
  }

  // Always show confirmation popup on first click
  setShowConfirmationDialog(true);
};

 const handleConfirmationContinue = () => {
  setShowConfirmationDialog(false);

  const updatedData = allData.map(row => {
    const filteredRow = filteredData.find(
      (fr, idx) =>
        fr.locationId === row.locationId &&
        isPrimaryLocationRow(idx)
    );

    if (
      filteredRow &&
      !filteredRow.isLocked &&
      (filteredRow.noInventory || filteredRow.onHand > 0)
    ) {
      return {
        ...row,
        newSequence: filteredRow.newSequence || 'A99',
        wasAutoFilled: !filteredRow.newSequence
      };
    }

    return row;
  });

  setAllData(updatedData);

  // mark confirmation completed
  setHasConfirmedUpdate(true);

  showToast(
    "Review the values and click 'Update MA' again to save.",
    'info'
  );
};
  
  const handleConfirmationCancel = () => {
    setShowConfirmationDialog(false);
  };

  /* -------------------- Derived Data -------------------- */

  const filteredData = useMemo(() => {
  return allData.filter(row =>
    (
      row.noInventory ||
      showEmpty ||
      row.onHand > 0
    ) &&
    (!row.isLocked || showLocked)
  );
}, [allData, showLocked, showEmpty]);

const firstLocationIndexes = useMemo(() => {
  const seen = new Set<string>();
  const indexes = new Set<number>();

  filteredData.forEach((row, index) => {
    if (!seen.has(row.locationId)) {
      seen.add(row.locationId);
      indexes.add(index);
    }
  });

  return indexes;
}, [filteredData]);

const isPrimaryLocationRow = useCallback(
  (rowIndex: number) => firstLocationIndexes.has(rowIndex),
  [firstLocationIndexes]
);

  const validateAllFields = useCallback(() => {
    const newErrors: Record<string, string> = {};
    let errorFound = false;
    let topError: string | null = null;

    // Step 1: Validate format for each cell individually
    filteredData.forEach((row, i) => {

      
if (!isPrimaryLocationRow(i)) {
    return;
  }

      const currentVal = row.newSequence;
      const key = `${i}-col_6`; // Corrected for new column order

      if (currentVal) {
        if (/^A0+$/.test(currentVal)) {
          newErrors[key] = 'Invalid format';
          errorFound = true;
          if (!topError) topError = 'Invalid format found.';
        } else if (currentVal !== 'A' && !/^A(0[1-9]|[1-9]\d*)$/.test(currentVal)) {
          newErrors[key] = 'Invalid format';
          errorFound = true;
          if (!topError) topError = 'Invalid format found.';
        } else if (filteredData.length > 1 && currentVal === 'A') {
          newErrors[key] = 'Invalid sequence';
          errorFound = true;
          if (!topError) topError = "'A' is an invalid sequence when multiple rows exist.";
        } else {
          const numericValue = parseInt(currentVal.substring(1), 10);
          if (!isNaN(numericValue) && numericValue > 99) {
            newErrors[key] = 'Max value is A99';
            errorFound = true;
            if (!topError) topError = 'Values cannot exceed A99.';
          }
        }
      }

    });

    // Step 2: Check for duplicates across all valid-format fields
    const map = new Map<string, number[]>();
    filteredData.forEach((r, i) => {
      
 if (!isPrimaryLocationRow(i)) {
    return;
  }

      const currentVal = r.newSequence;
      if (currentVal && currentVal !== 'A99' && !newErrors[`${i}-col_6`]) { // Corrected for new column order
        map.set(currentVal, [...(map.get(currentVal) || []), i]);
      }
    });

    map.forEach((indices) => {
      if (indices.length > 1) {
        indices.forEach(i => {
          newErrors[`${i}-col_6`] = 'Duplicate'; // Corrected for new column order
        });
        errorFound = true;
        if (!topError) topError = 'Duplicate values are not allowed in Pick Execution Sequence.';
      }
    });


    setValidationErrors(newErrors);
    setTopLevelError(topError);
    setIsUpdateDisabled(errorFound);

    return !errorFound;
  }, [filteredData,isPrimaryLocationRow]);

  const updateCell = useCallback(
  (rowIndex: number, value: string) => {
    const locationId = filteredData[rowIndex]?.locationId;

    if (!locationId) return;

    setAllData(prev =>
      prev.map(row =>
        row.locationId === locationId
          ? { ...row, newSequence: value }
          : row
      )
    );
  },
  [filteredData]
);


  const handleSequenceValueChange = useCallback((rowIndex: number, value: any, field: 'newSequence') => {
    if (typeof value !== 'string') return;

    const sanitized = String(value)
  .replace(/[^a-zA-Z0-9]/g, '')
  .toUpperCase();
   setHasConfirmedUpdate(false);
updateCell(rowIndex, sanitized);
  }, [updateCell]);

  const validateField = useCallback((value: string, rowIndex: number, field: 'newSequence') => {
    if (!isPrimaryLocationRow(rowIndex)) {
  return true;
}
    const newErrors: Record<string, string> = { ...validationErrors };
    let errorFound = false;
    let topError: string | null = null;

    const colIndex = 6; // Corrected column index for newSequence

    // Clear errors for the current field being validated
    Object.keys(newErrors).forEach(key => {
      if (key.startsWith(`${rowIndex}-col_${colIndex}`)) {
        delete newErrors[key];
      }
    });

    // Step 1: Validate format for the changed cell
    const key = `${rowIndex}-col_${colIndex}`;
    if (value) {
      if (/^A0+$/.test(value)) {
        newErrors[key] = 'Invalid format';
      } else if (value !== 'A' && !/^A(0[1-9]|[1-9]\d*)$/.test(value)) {
        newErrors[key] = 'Invalid format';
      } else if (filteredData.length > 1 && value === 'A') {
        newErrors[key] = 'Invalid sequence';
      } else {
        const numericValue = parseInt(value.substring(1), 10);
        if (!isNaN(numericValue) && numericValue > 99) {
          newErrors[key] = 'Max value is A99';
        }
      }
    }

    // Step 2: Check for duplicates across all valid-format fields for the current field type
    const map = new Map<string, number[]>();
    filteredData.forEach((r, i) => {
      if (!isPrimaryLocationRow(i)) {
  return;
}
      const currentVal = i === rowIndex ? value : r[field];
      const currentKey = `${i}-col_${colIndex}`;
      // Only consider non-errored (or the current) fields for duplicate check
      if (currentVal && currentVal !== 'A99' && (!newErrors[currentKey] || i === rowIndex)) {
        map.set(currentVal, [...(map.get(currentVal) || []), i]);
      }
    });

    // Clear previous duplicate errors for this field type before adding new ones
    Object.keys(newErrors).forEach(k => {
        if (newErrors[k] === 'Duplicate' && k.endsWith(`-col_${colIndex}`)) {
            delete newErrors[k];
        }
    });

    map.forEach((indices, val) => {
      if (indices.length > 1) {
        if (!hasShownDuplicateRef.current) {
          showToast(`Duplicate value "${val}" is not allowed.`, 'error');
          hasShownDuplicateRef.current = true;
          setTimeout(() => { hasShownDuplicateRef.current = false; }, 2000);
        }
        indices.forEach(i => {
          newErrors[`${i}-col_${colIndex}`] = 'Duplicate';
        });
      }
    });

    // Re-check for top-level error message
    Object.keys(newErrors).forEach(k => {
      if (newErrors[k]) {
        errorFound = true;
        if (!topError) {
          if (newErrors[k] === 'Duplicate') topError = 'Duplicate values are not allowed.';
          else if (newErrors[k] === 'Invalid format') topError = 'Invalid format found.';
          else if (newErrors[k] === 'Max value is A99') topError = 'Values cannot exceed A99.';
          else if (newErrors[k] === 'Invalid sequence') topError = 'Invalid sequence for multiple rows.';
        }
      }
    });

    setValidationErrors(newErrors);
    setTopLevelError(topError);
    setIsUpdateDisabled(Object.keys(newErrors).length > 0);

    return !errorFound;
  }, [filteredData, showToast, validationErrors, isPrimaryLocationRow]);

  const handleSequenceBlur = useCallback((rowIndex: number, field: 'newSequence') => {
    const row = filteredData[rowIndex];
    if (!row) return;

    let value = row[field];

    if (value) {
      // If value is purely numeric, format it.
      if (/^\d+$/.test(value)) {
        const numericValue = parseInt(value, 10);
        if (numericValue > 99) {
          value = 'A99';
        } else if (numericValue < 10) {
          value = 'A0' + numericValue;
        } else {
          value = 'A' + numericValue;
        }
      } 
      // If it's not numeric and doesn't start with 'A', try to salvage numbers.
      else if (!value.startsWith('A')) {
        const numbers = value.replace(/[^0-9]/g, '');
        if (numbers) {
          const numericValue = parseInt(numbers, 10);
          if (numericValue > 99) {
            value = 'A99';
          } else if (numericValue < 10) {
            value = 'A0' + numericValue;
          } else {
            value = 'A' + numericValue;
          }
        }
      }
      // Format single-digit entries like A1 to A01, but not A10, A99 etc.
      else if (/^A\d$/.test(value)) {
        value = 'A0' + value.substring(1);
      }
    }
    
    if (value !== row[field]) {
      updateCell(rowIndex, value);
      // Pass the newly formatted value to validation
      validateField(value, rowIndex, field);
    } else {
      // Value hasn't changed, but still run validation
      validateField(value, rowIndex, field);
    }
  }, [filteredData, updateCell, validateField]);

  const handleClearCell = useCallback((rowIndex: number, field: 'newSequence') => {
    updateCell(rowIndex, '');
    validateField('', rowIndex, field);
  }, [updateCell, validateField]);

  useEffect(() => {
    setTableData(
      filteredData.map((r, index) => {
        const displayLocation = (
          <div className="location-display">
            <span className="location-id-text">{r.locationId}</span>
          </div>
        );


        const statusCell =
          showLocked && r.isLocked ? (
            <div className="status-cell">
              {r.conditionCode && (
                <Chip
                  label={r.conditionCode}
                  size="small"
                  variant="outlined"
                  color={
                    r.conditionCode === 'DMG'
                      ? 'error'
                      : r.conditionCode === 'HLD'
                      ? 'warning'
                      : 'default'
                  }
                  sx={{
                    height: '20px',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    borderRadius: '4px'
                  }}
                />
              )}
              <Lock className="lock-icon" size={14} />
            </div>
          ) : (
            <div className="status-cell">-</div>
          );
  
        const displayOldSequence = (
          <div className="old-sequence-badge">
            {r.currentSequence}
          </div>
        );

        const displayOldAllocationSequence = (
          <div className="old-sequence-badge">
            {r.currentAllocationSequence}
          </div>
        );
  
        const isReadOnly =
  r.isLocked ||
  (!r.noInventory && Number(r.onHand) <= 0) ||
  !isPrimaryLocationRow(index);

const newSequenceCell = isReadOnly ? (
  <div className="old-sequence-badge">
    {r.newSequence || '-'}
  </div>
) : (
  <div className="new-seq-cell-wrapper">
    <input
      type="text"
      value={r.newSequence}
      onChange={(e) =>
        handleSequenceValueChange(index, e.target.value, 'newSequence')
      }
      onBlur={() => handleSequenceBlur(index, 'newSequence')}
      className="sequence-input"
      onClick={(e) => e.stopPropagation()}
    />
    {r.newSequence && (
      <XCircle
        className="clear-icon"
        size={16}
        onClick={() => handleClearCell(index, 'newSequence')}
      />
    )}
  </div>
);

const getGradientForItem = (itemId: string) => {
  let hash = 0;

  for (let i = 0; i < itemId.length; i++) {
    hash = ((hash << 5) - hash) + itemId.charCodeAt(i);
    hash |= 0;
  }

  const hue = Math.abs(hash) % 360;

  return {
    background: `linear-gradient(
      135deg,
      hsl(${hue}, 85%, 90%),
      hsl(${(hue + 25) % 360}, 85%, 78%)
    )`
  };
};

const displayItemsInventory = r.noInventory ? (
  <div className="empty-item">-</div>
) : (
  <div className="nested-item-container">
    {r.cicDetails.map((cic) => (
      <div
  key={cic.ItemId}
  className="inventory-card"
  style={getGradientForItem(cic.ItemId)}
>
        <div className="inventory-item-id">
          {cic.ItemId}
        </div>

        <div className="inventory-onhand">
          {Math.floor(cic.OnHand)} packs
        </div>
      </div>
    ))}
  </div>
);



       return [
  displayLocation,
  displayItemsInventory,
  statusCell,
  displayOldAllocationSequence,
  displayOldSequence,
  newSequenceCell
];
      })
    );
  }, [filteredData, dataVersion, handleSequenceValueChange, handleSequenceBlur, showLocked, handleClearCell,isPrimaryLocationRow]);


  /* -------------------- Validation -------------------- */

  useEffect(() => {
    const hasErrors = Object.keys(validationErrors).length > 0;
    setIsUpdateDisabled(hasErrors);
  }, [validationErrors]);


  /* -------------------- Handlers -------------------- */

  const handleCellClick = (i: number, col: string) => {
    // if (col === 'Pick Execution Sequence - New' && !filteredData[i]?.newSequence) {
    //   updateCell(i, 'A01');
    // }
  };


  const handleCellEdit = (i: number, col: string, value: string) => {
    // This is now handled by handleSequenceValueChange, but we keep the function
    // to satisfy the EditableTable prop, just in case. It won't be called for our custom cell.
  };

  const handleReset = () => {
    if (isResettingRef.current) return;
    isResettingRef.current = true;

    setAllData(prev =>
      prev.map(row => ({
        ...row,
        newSequence: '',
      }))
    );
    setShowLocked(false);
    setShowEmpty(false);
    setValidationErrors({});
    setTopLevelError(null);
    setIsUpdateDisabled(true);
    setHasConfirmedUpdate(false);
    showToast('All new sequences have been cleared.', 'info');

    setTimeout(() => {
      isResettingRef.current = false;
    }, 500);
  };


  const isCellDisabled = (rowIndex: number, col: string) => {
  if (col !== 'Pick Execution Sequence - New') return false;

  const rowData = filteredData[rowIndex];

  return !!rowData &&
(
  rowData.isLocked ||
  (!rowData.noInventory && rowData.onHand === 0) ||
  !isPrimaryLocationRow(rowIndex)
);
};

  /* -------------------- Effects -------------------- */

  useEffect(() => {
  if (selectedOrg && prevOrgRef.current && prevOrgRef.current !== selectedOrg) {
    // Only reset when actual org changes
    setShowLocked(false);
  }

  prevOrgRef.current = selectedOrg;
}, [selectedOrg]);


  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      if (!isCancelled) {
        await fetchBananaRooms();
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
      setIsLoading(false);
    };
  }, [fetchBananaRooms, accounts]);


  /* -------------------- UI -------------------- */

  return (
    
    <div className="banana-sequencing-container">
      {topLevelError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {topLevelError}
        </Alert>
      )}
      {/* Only show header controls when there is actually data loaded, or if it is currently loading */}
      {(isLoading || allData.length > 0) && (
        <div className="banana-sequencing-header">
          <div className="header-left-controls" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <motion.div 
              className="fancy-toggle-wrapper"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showLocked}
                    onChange={(e) => setShowLocked(e.target.checked)}
                    name="showLocked"
                    className="fancy-switch"
                  />
                }
                label={
                  <span className={`fancy-toggle-label ${showLocked ? 'active' : ''}`}>
                    Show Locked Locations
                  </span>
                }
              />
            </motion.div>
            <motion.div 
              className="fancy-toggle-wrapper"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showEmpty}
                    onChange={(e) => setShowEmpty(e.target.checked)}
                    name="showEmpty"
                    className="fancy-switch"
                  />
                }
                label={
                  <span className={`fancy-toggle-label ${showEmpty ? 'active' : ''}`}>
                    Show Empty Locations
                  </span>
                }
              />
            </motion.div>
            <div className="banana-info-banner-inline" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e3a8a', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '20px', padding: '6px 16px', fontSize: '0.825rem', fontWeight: 500 }}>
              <Info size={14} className="banana-info-icon" style={{ color: '#3b82f6' }} />
              <span>
                Lower numbers are picked first.
              </span>
            </div>
          </div>
          <div className="header-right-controls" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                onClick={fetchBananaRooms} 
                size="sm" 
                variant="tertiary"
                className="fancy-refresh-btn"
                disabled={isLoading}
              >
                <RefreshCw size={14} className={`btn-icon ${isLoading ? 'spin' : ''}`} />
                Refresh
              </Button>
            </motion.div>
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={!isUpdateDisabled && !isLoading ? { scale: 1.05 } : {}}
              whileTap={!isUpdateDisabled && !isLoading ? { scale: 0.95 } : {}}
            >
              <Button 
                onClick={handleUpdate} 
                size="sm" 
                disabled={isUpdateDisabled || isLoading}
                className={`fancy-update-btn ${isUpdateDisabled || isLoading ? 'disabled' : ''}`}
              >
                <ArrowUpCircle size={15} className="btn-icon" />
                Update MA
              </Button>
            </motion.div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="banana-loading-container">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
            style={{ display: 'inline-block' }}
          >
            <RefreshCw size={32} style={{ color: '#4f46e5' }} />
          </motion.div>
          <p className="banana-loading-text">Fetching sequencing details...</p>
        </div>
      ) : allData.length === 0 ? (
        <motion.div 
          className="banana-no-data-card"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="no-data-icon-wrapper">
            <Info size={40} className="no-data-icon" />
          </div>
          <h3 className="no-data-title">No Banana Rooms Allocated</h3>
          <p className="no-data-description">
            Banana rooms are not allocated at the moment. Please check back later or try checking again.
          </p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={fetchBananaRooms}
              className="fancy-no-data-refresh-btn"
              size="sm"
            >
              <RefreshCw size={14} className="btn-icon" />
              Check Again
            </Button>
          </motion.div>
        </motion.div>
      ) : (
        <div className="banana-table-theme">
          <div className="banana-table-shrink">
            
<div className="sequence-update-note">
  <span className="sequence-note-asterisk">*</span>
  <span>
    <strong>New Sequence</strong> updates both Pick Allocation Sequence and Pick Execution Sequence.
  </span>
</div>

            <EditableTable
              headers={HEADERS}
              data={tableData as string[][]}
              onDataChange={() => {}}
              onCellEdit={handleCellEdit}
              onCellClick={handleCellClick}
              showPaginationControls={true}
              showEditControls={false}
              nonEditableColumns={[
  'Location ID',
  'On Hand',
  'Condition Code(s)',
  'Current Pick Allocation Sequence',
  'Current Pick Execution Sequence',
  'New Sequence *'
]}
              validationErrors={validationErrors}
              isCellDisabled={isCellDisabled}
            />
          </div>

          <div className="banana-reset-wrapper">
            <div className="button-container">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="secondary"
                  onClick={handleReset}
                  className="fancy-refresh-btn"
                  size="sm"
                >
                  <RotateCcw size={14} className="btn-icon" />
                  Reset
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={showConfirmationDialog}
        onClose={handleConfirmationCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '10px',
            padding: '0.5rem',
            minWidth: '320px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle 
          id="alert-dialog-title" 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.05rem', 
            fontWeight: 600, 
            padding: '12px 16px',
            color: '#4f46e5'
          }}
        >
          <AlertTriangle size={18} />
          Confirm Update
        </DialogTitle>
        <DialogContent sx={{ padding: '0 16px' }}>
          <DialogContentText 
            id="alert-dialog-description" 
            sx={{ 
              color: '#374151',
              fontWeight: 500, 
              fontSize: '0.9rem',
              backgroundColor: '#f3f4f6',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              textAlign: 'center',
              lineHeight: 1.5 
            }}
          >
            Empty cells will be replaced with the default value 'A99'. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 16px 12px' }}>
          <Button onClick={handleConfirmationCancel} variant="tertiary" size='sm'>Cancel</Button>
          <Button onClick={handleConfirmationContinue} size='sm' className="fancy-update-btn">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BananaSequencing;
